/**
 * สี (Brand Colors) — สีที่บอตใช้ตอบ "มีสีอะไร / สีนี้มีไหม" + ผูกรูปสินค้า + สต๊อกรายไซซ์
 * เชื่อมกับ: รูปสินค้า (product_images.color = color_key) · ทดสอบบอต ("มีสีเขียวไหม")
 *
 * ใช้ CrudManager กลาง — ตารางสีหลัก + panel สต๊อกรายไซซ์ต่อสี (renderRowExtra, แต่ละสีเลือกไซซ์เองได้)
 */
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2 } from 'lucide-react';
import { CrudManager, type CrudField } from '@/components/CrudManager';
import { BrandColorsApi, BrandSizesApi, ColorSizeStockApi, type BrandColorItem, type Brand } from '@/lib/admin-api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';

const FIELDS: CrudField[] = [
  { key: 'colorKey', label: 'รหัสสี (key)', type: 'text', required: true, placeholder: 'green', disabledOnEdit: true, help: 'อังกฤษ ใช้ผูกกับรูปสินค้า เช่น green, navy, gray_light' },
  { key: 'thaiNames', label: 'ชื่อที่ลูกค้าพิมพ์', type: 'chips', required: true, placeholder: 'เขียว, เขียวหัวเป็ด', help: 'ลูกค้าพิมพ์อันไหนก็ match (contains) คั่นด้วย ,' },
  { key: 'displayLabel', label: 'ชื่อแสดงในคำตอบ', type: 'text', placeholder: 'เขียว 💚', help: 'ใช้แทน thai_names เวลาบอตตอบ (ใส่ emoji ได้)' },
  { key: 'aliases', label: 'คำเรียกอื่น (alias)', type: 'chips', placeholder: 'ซิลเวอร์, silver', help: 'คำพ้อง เช่น เทา → ซิลเวอร์' },
  { key: 'inStock', label: 'มีของ (สีนี้)', type: 'toggle', default: true, help: 'สถานะสต๊อกหลักของสี — ถ้าหมด บอตตอบ “หมดชั่วคราว” ทุกไซซ์' },
  { key: 'restockNote', label: 'โน้ตตอนหมด', type: 'text', placeholder: 'กลับมาประมาณ 10 วัน', help: 'ข้อความบอกลูกค้าเมื่อสีนี้หมด' },
  { key: 'sortOrder', label: 'ลำดับ', type: 'number', default: 0, help: 'เลขน้อยขึ้นก่อน' },
  { key: 'active', label: 'เปิดใช้งาน', type: 'toggle', default: true },
];

export default function ColorsPage() {
  return (
    <CrudManager<BrandColorItem>
      title="🎨 สี (Brand Colors)"
      addLabel="เพิ่มสี"
      help="ลูกค้าพิมพ์ “ขอดูสีเขียว” → บอตเทียบ thai_names → ดึงรูปจาก product_images (color=key) ส่งให้. กดขยายแต่ละสีเพื่อจัดการสต๊อกรายไซซ์"
      queryKey="brand-colors"
      api={BrandColorsApi}
      fields={FIELDS}
      brandScoped
      searchKeys={['colorKey', 'thaiNames', 'displayLabel', 'aliases']}
      sortRows={(a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || a.id - b.id}
      rowTitle={(c) => <>{c.displayLabel || c.thaiNames}</>}
      rowBadges={(c) => (c.inStock ? <Badge variant="outline" className="text-[10px] text-green-700 border-green-300">✅ มีของ</Badge> : <Badge variant="outline" className="text-[10px] text-orange-700 border-orange-300">🟠 หมด</Badge>)}
      rowSubtitle={(c) => <>รหัส: <code>{c.colorKey}</code> · พิมพ์: {c.thaiNames}{c.aliases ? ` · alias: ${c.aliases}` : ''}</>}
      renderRowExtra={(c) => <SizeStockPanel brand={c.brand} colorKey={c.colorKey} colorLabel={c.displayLabel || c.thaiNames} />}
    />
  );
}

// ============================================================
// SizeStockPanel — จัดการสต๊อกรายไซซ์ของ "สีเดียว" (แต่ละสีเลือกไซซ์เองได้)
// ============================================================
function SizeStockPanel({ brand, colorKey, colorLabel }: { brand: Brand; colorKey: string; colorLabel: string }) {
  const qc = useQueryClient();
  const [newSize, setNewSize] = useState('');
  const stockQ = useQuery({ queryKey: ['color-size-stock', brand, colorKey], queryFn: () => ColorSizeStockApi.list(brand, colorKey) });
  const sizesQ = useQuery({ queryKey: ['brand-sizes', brand], queryFn: () => BrandSizesApi.list(brand) });
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['color-size-stock', brand, colorKey] });
    qc.invalidateQueries({ queryKey: ['brand-sizes', brand] });
  };
  const upsertMut = useMutation({
    mutationFn: (d: { size_label: string; in_stock: number; note?: string | null; sort_order?: number }) => ColorSizeStockApi.upsert({ brand, color_key: colorKey, ...d }),
    onSuccess: invalidate,
    onError: (e: Error) => toast.error('ผิดพลาด: ' + e.message),
  });
  const removeMut = useMutation({ mutationFn: (size: string) => ColorSizeStockApi.remove(brand, colorKey, size), onSuccess: invalidate });
  const addSizeMut = useMutation({
    mutationFn: (label: string) => BrandSizesApi.create({ brand, size_label: label, sort_order: (sizesQ.data?.length ?? 0) + 1 }),
    onSuccess: () => { setNewSize(''); qc.invalidateQueries({ queryKey: ['brand-sizes', brand] }); toast.success('เพิ่มไซซ์เข้าคลังแล้ว'); },
    onError: (e: Error) => toast.error('ผิดพลาด: ' + e.message),
  });
  const stock = stockQ.data ?? [];
  const offered = new Set(stock.map((s) => s.size_label));
  const poolNotOffered = (sizesQ.data ?? []).filter((s) => s.active && !offered.has(s.size_label));
  return (
    <div className="space-y-3">
      <div className="text-sm font-medium">📏 สต๊อกรายไซซ์ของ <span className="text-primary">{colorLabel}</span>
        <span className="ml-2 text-xs font-normal text-muted-foreground">แต่ละสีเลือกไซซ์ของตัวเองได้ · กดเปิด/ปิด = มีของ/หมด</span>
      </div>
      {stockQ.isLoading ? <div className="text-xs text-muted-foreground">กำลังโหลด…</div>
        : stock.length === 0 ? <div className="text-xs text-muted-foreground">ยังไม่ได้เลือกไซซ์ให้สีนี้ — เพิ่มจากคลังด้านล่าง</div>
        : (
          <div className="flex flex-wrap gap-2">
            {stock.map((s) => (
              <div key={s.size_label} className={`flex items-center gap-1.5 rounded-lg border px-2 py-1 text-xs ${s.in_stock ? 'bg-green-50 border-green-300' : 'bg-orange-50 border-orange-300'}`}>
                <span className="font-bold w-8 text-center">{s.size_label}</span>
                <button className={`px-1.5 py-0.5 rounded border ${s.in_stock ? 'bg-green-100 text-green-700 border-green-300' : 'bg-orange-100 text-orange-700 border-orange-300'}`} title="กดสลับ มีของ/หมด"
                  onClick={() => {
                    if (s.in_stock) { const note = prompt(`ทำให้ ${colorLabel} ไซซ์ ${s.size_label} = "หมด" — โน้ตบอกลูกค้า (เว้นว่างได้):`, s.note ?? ''); if (note === null) return; upsertMut.mutate({ size_label: s.size_label, in_stock: 0, note, sort_order: s.sort_order }); }
                    else upsertMut.mutate({ size_label: s.size_label, in_stock: 1, note: null, sort_order: s.sort_order });
                  }}>
                  {s.in_stock ? '✅ มีของ' : '🟠 หมด'}
                </button>
                {!s.in_stock && s.note && <span className="text-[10px] text-muted-foreground">{s.note}</span>}
                <button className="text-red-500 hover:text-red-700" title="เอาไซซ์นี้ออก" onClick={() => confirm(`เอาไซซ์ ${s.size_label} ออกจากสี ${colorLabel}?`) && removeMut.mutate(s.size_label)}><Trash2 size={12} /></button>
              </div>
            ))}
          </div>
        )}
      {poolNotOffered.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5 text-xs">
          <span className="text-muted-foreground">เพิ่มไซซ์ให้สีนี้:</span>
          {poolNotOffered.map((s) => (
            <button key={s.id} className="px-2 py-0.5 rounded-full border border-dashed border-primary/50 text-primary hover:bg-primary/10" onClick={() => upsertMut.mutate({ size_label: s.size_label, in_stock: 1, sort_order: s.sort_order })}>+ {s.size_label}</button>
          ))}
        </div>
      )}
      <div className="flex items-center gap-2 border-t border-border pt-2">
        <Input value={newSize} onChange={(e) => setNewSize(e.target.value)} placeholder="ไซซ์ใหม่เข้าคลัง เช่น 3XL" className="h-8 w-40 text-xs" />
        <Button size="sm" variant="outline" className="h-8" disabled={!newSize.trim()} onClick={() => addSizeMut.mutate(newSize.trim())}><Plus size={12} className="mr-1" /> เพิ่มเข้าคลังไซซ์</Button>
        <span className="text-[10px] text-muted-foreground">เพิ่มแล้วใช้ได้ทุกสี</span>
      </div>
    </div>
  );
}
